<script lang="ts">
  import { _ } from 'svelte-i18n';
  import {
    cantons,
    centerPostalCode,
    postalCodes,
    radiusKm,
    languages
  } from '$lib/stores/selection';

  /**
   * Embedded in a screen that has already asked the question.
   *
   * The sidebar shows this as one collapsed section among many, so it carries
   * its own heading and an "are we filtering by region at all" checkbox. The
   * guided search screen devotes a whole step to the question, where that
   * checkbox would be asking it twice and the heading would repeat the step's
   * own title.
   *
   * Only the chrome differs. Every panel below writes to the same selection
   * stores either way, which is what makes the component reusable at all.
   */
  export let embedded = false;

  /**
   * The five ways of saying where, in the order they are offered.
   *
   * One list rather than five near-identical blocks of markup: the pills they
   * replace differed only in their value and their label, and each carried its
   * own copy of eleven utility classes.
   */
  const regionTypes = [
    { id: 'radius', label: 'filters.regionTypes.radius' },
    { id: 'plzList', label: 'filters.regionTypes.plzList' },
    { id: 'cantons', label: 'filters.regionTypes.cantons' },
    { id: 'regionPreset', label: 'filters.regionTypes.regions' },
    { id: 'languages', label: 'filters.regionTypes.languages' }
  ];

  /**
   * The seven Grossregionen, which is the grouping Switzerland actually uses.
   *
   * # What was wrong with the five that were here
   *
   * They left out Zürich and Ticino entirely. Every other canton appeared in
   * one of the five, so the omission read as a grouping choice rather than a
   * gap — and it hid the largest market in the country: measured against the
   * corpus, 140,208 active companies sit in Zürich and 38,833 in Ticino, so
   * 179,041 companies could not be reached from this panel at all. A customer
   * looking for the Zurich area had to know to switch to the canton list.
   *
   * # Why the federal grouping rather than a hand-made one
   *
   * These are the BFS Grossregionen: seven regions covering all twenty-six
   * cantons exactly once, and the names a Swiss business reader already knows.
   * The previous five were close to them but not them — Aargau sat in the
   * Mittelland rather than the Nordwestschweiz, Neuchâtel and Jura in the
   * Westschweiz rather than the Espace Mittelland — which is a distinction
   * nobody outside this file could have known about.
   *
   * The cantons are printed beside each name, so the grouping is never a claim
   * the customer has to take on trust.
   */
  const regionPresets = [
    { id: 'zurich', cantons: ['ZH'] },
    { id: 'espaceMittelland', cantons: ['BE', 'FR', 'SO', 'NE', 'JU'] },
    { id: 'northwesternSwitzerland', cantons: ['BS', 'BL', 'AG'] },
    { id: 'centralSwitzerland', cantons: ['LU', 'UR', 'SZ', 'OW', 'NW', 'ZG'] },
    { id: 'easternSwitzerland', cantons: ['GL', 'SH', 'AR', 'AI', 'SG', 'GR', 'TG'] },
    { id: 'lakeGeneva', cantons: ['VD', 'VS', 'GE'] },
    { id: 'ticino', cantons: ['TI'] }
  ];

  let useRegionFilter = false;
  let selectedType = 'radius';
  let postalCodeInput = '';

  function addPostalCodes() {
    const newCodes = postalCodeInput
      .split(',')
      .map((s) => s.trim())
      .filter((code) => code.length && !$postalCodes.includes(code));

    if (newCodes.length) {
      postalCodes.set([...$postalCodes, ...newCodes]);
    }

    postalCodeInput = ''; // clear input
  }

  // Handle checkbox toggle
  function toggleRegionFilter() {
    useRegionFilter = !useRegionFilter;
    if (!useRegionFilter) {
      // Clear all regional filters when unchecking
      centerPostalCode.set('');
      radiusKm.set(0);
      postalCodes.set([]);
      cantons.set([]);
      languages.set([]);
      selectedType = 'radius'; // Reset to default
    }
  }

  // Handle type selection change
  // Called from the chips below, never from a real change event: every call
  // site used to build a fake `{ target: { value } }` to satisfy a signature
  // that nothing actually invoked as a handler.
  function handleTypeChange(newType: string) {
    const oldType = selectedType;
    selectedType = newType;

    // Clear only the filters from the previous type when switching types.
    //
    // `useRegionFilter` is false when embedded — the step's existence is the
    // opt-in there — so gating on it alone left the previous type's values
    // applied and invisible: switch from cantons to languages in the guide and
    // the cantons were still narrowing the search.
    if ((useRegionFilter || embedded) && oldType !== newType) {
      if (oldType === 'radius') {
        centerPostalCode.set('');
        radiusKm.set(0);
      } else if (oldType === 'plzList') {
        postalCodes.set([]);
      } else if (oldType === 'cantons') {
        cantons.set([]);
      } else if (oldType === 'languages') {
        languages.set([]);
      }
    }
  }

  // Sync checkbox when filters are set externally (e.g. by AI)
  // Only auto-enable when stores have values; never auto-disable
  // (user may have just checked the box but not entered values yet)
  $: {
    const hasRegion =
      ($centerPostalCode && $radiusKm > 0) ||
      $postalCodes.length > 0 ||
      $cantons.length > 0 ||
      $languages.length > 0;
    if (hasRegion && !useRegionFilter) {
      useRegionFilter = true;
      // Auto-select the correct tab based on which filter was set
      if ($cantons.length > 0) selectedType = 'cantons';
      else if ($postalCodes.length > 0) selectedType = 'plzList';
      else if ($languages.length > 0) selectedType = 'languages';
      else if ($centerPostalCode && $radiusKm > 0) selectedType = 'radius';
    }
  }
</script>

<div class={embedded ? '' : useRegionFilter ? 'm-2 rounded-control bg-surface-well p-3' : 'p-3'}>
  <!-- Region Filter Toggle -->
  {#if !embedded}
    <div class="mb-3">
      <label class="group flex cursor-pointer items-center">
        <input
          type="checkbox"
          checked={useRegionFilter}
          on:click={toggleRegionFilter}
          class="mr-2 h-3 w-3 rounded border-gray-300 text-accent-600 focus:ring-1 focus:ring-accent-200"
        />
        <svg class="filter-mark mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
          />
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        <span
          class="text-xs font-semibold text-gray-900 transition-colors group-hover:text-gray-700"
        >
          {$_('filters.regional')}
        </span>
      </label>
    </div>
  {/if}

  {#if useRegionFilter || embedded}
    <!--
      A dropdown, not pills.

      These five were mutually exclusive — picking one clears the others — and
      pills say "choose any" while a select says "choose one". They were also
      <span role="button" tabindex="0"> with a hand-rolled Enter handler, which
      is a select rebuilt badly: no arrow keys, no type-ahead, no native
      announcement of how many options there are or which is chosen.
    -->
    {#if embedded}
      <!--
        Pills here, a dropdown in the sidebar.

        The sidebar's five options are a dropdown because they are mutually
        exclusive and the column is 320px wide: five pills there wrap onto three
        lines and eat the space the filter itself needs. A guided step has the
        width of the card and one question on it, where a dropdown hides four of
        the five ways to answer behind a click and reads as a form field on a
        screen that has no other form fields.

        Same exclusivity either way — one is selected, choosing another clears
        the first — which is why they are `aria-pressed` buttons in a radiogroup
        rather than checkboxes.
      -->
      <div class="mb-4 flex flex-wrap gap-2" role="radiogroup" aria-label={$_('filters.regional')}>
        {#each regionTypes as type (type.id)}
          <button
            type="button"
            role="radio"
            aria-checked={selectedType === type.id}
            on:click={() => handleTypeChange(type.id)}
            class="rounded-full border px-4 py-1.5 text-sm transition-colors {selectedType ===
            type.id
              ? 'border-accent-500 bg-accent-50 font-medium text-accent-700'
              : 'border-hairline text-gray-600 hover:border-accent-300 hover:bg-gray-50'}"
          >
            {$_(type.label)}
          </button>
        {/each}
      </div>
    {:else}
      <div class="mb-3">
        <label class="sr-only" for="region-type">{$_('filters.regional')}</label>
        <select
          id="region-type"
          class="w-full px-2 py-1.5 text-xs"
          value={selectedType}
          on:change={(e) => handleTypeChange(e.currentTarget.value)}
        >
          {#each regionTypes as type (type.id)}
            <option value={type.id}>{$_(type.label)}</option>
          {/each}
        </select>
      </div>
    {/if}

    <!-- Radius Filter -->
    {#if selectedType === 'radius'}
      <div class="space-y-3 {embedded ? 'max-w-sm' : ''}">
        <div>
          <label class="mb-1 block text-xs font-medium text-gray-700"
            >{$_('filters.postalCode.center')}</label
          >
          <input
            type="text"
            bind:value={$centerPostalCode}
            class="w-full rounded-control border border-gray-300 px-3 py-1.5 text-xs placeholder-gray-400 focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
            placeholder={$_('filters.postalCode.placeholder').split(',')[0]}
          />
        </div>

        <div>
          <label class="mb-1 block text-xs font-medium text-gray-700"
            >{$_('filters.postalCode.radius')}</label
          >
          <div class="flex items-center gap-2">
            <input
              type="range"
              bind:value={$radiusKm}
              min="0"
              max="100"
              class="h-1 flex-1 cursor-pointer appearance-none rounded-control bg-gray-200 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-accent-500 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-accent-500"
            />
            <input
              type="number"
              bind:value={$radiusKm}
              min="0"
              max="100"
              class="w-12 rounded-control border border-gray-300 px-2 py-1 text-center text-xs focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
            />
          </div>
        </div>
      </div>
    {/if}

    <!-- PLZ List -->
    {#if selectedType === 'plzList'}
      <div class="space-y-2 {embedded ? 'max-w-md' : ''}">
        <div class="mb-2 flex items-center justify-between">
          <label class="block text-xs font-medium text-gray-700"
            >{$_('filters.postalCode.codes')}</label
          >
          <span class="text-2xs text-gray-500">{$postalCodes.length} PLZ</span>
        </div>

        <div class="flex gap-1">
          <input
            type="text"
            bind:value={postalCodeInput}
            placeholder={$_('filters.postalCode.placeholder')}
            class="flex-1 rounded-control border border-gray-300 px-2 py-1.5 text-xs placeholder-gray-400 focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
            on:keypress={(e) => e.key === 'Enter' && postalCodeInput.trim() && addPostalCodes()}
          />
          <div
            on:click={() => postalCodeInput.trim() && addPostalCodes()}
            role="button"
            tabindex="0"
            on:keydown={(e) => e.key === 'Enter' && postalCodeInput.trim() && addPostalCodes()}
            class="cursor-pointer rounded-control bg-accent-50 px-3 py-1.5 text-xs font-medium text-accent-700 ring-1 ring-inset ring-accent-700/10 transition-colors hover:bg-accent-100 {!postalCodeInput.trim()
              ? 'cursor-not-allowed opacity-50'
              : ''}"
          >
            +
          </div>
        </div>

        {#if $postalCodes.length}
          <div class="flex flex-wrap gap-1">
            {#each $postalCodes as code}
              <span
                class="inline-flex items-center gap-1 rounded-full bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-800"
              >
                {code}
                <button
                  type="button"
                  on:click={() => postalCodes.update((codes) => codes.filter((c) => c !== code))}
                  class="text-accent-600 hover:text-accent-800"
                >
                  ×
                </button>
              </span>
            {/each}
          </div>
        {/if}
      </div>
    {/if}

    <!-- Cantons -->
    {#if selectedType === 'cantons'}
      <div class="space-y-2">
        <div class="mb-2 flex items-center justify-between">
          <label class="block text-xs font-medium text-gray-700"
            >{$_('filters.canton.select')}</label
          >
          <span class="text-2xs text-gray-500"
            >{$cantons.length} {$_('buttons.select').toLowerCase()}</span
          >
        </div>

        <!-- Canton grid with same style as contact roles -->
        <div class="grid gap-1.5 {embedded ? 'grid-cols-4 sm:grid-cols-6' : 'grid-cols-2'}">
          {#each ['AG', 'AI', 'AR', 'BE', 'BL', 'BS', 'FR', 'GE', 'GL', 'GR', 'JU', 'LU', 'NE', 'NW', 'OW', 'SG', 'SH', 'SO', 'SZ', 'TG', 'TI', 'UR', 'VD', 'VS', 'ZG', 'ZH'] as code}
            <div
              class="group relative flex cursor-pointer items-center rounded-control border border-gray-200 bg-white px-3 py-2 transition-colors duration-200 hover:border-accent-300 hover:bg-accent-50 {$cantons.includes(
                code
              )
                ? 'border-accent-500 bg-accent-50'
                : ''}"
              on:click={() => {
                const isSelected = $cantons.includes(code);
                if (isSelected) {
                  cantons.set($cantons.filter((c) => c !== code));
                } else {
                  cantons.set([...$cantons, code]);
                }
              }}
              on:keydown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  const isSelected = $cantons.includes(code);
                  if (isSelected) {
                    cantons.set($cantons.filter((c) => c !== code));
                  } else {
                    cantons.set([...$cantons, code]);
                  }
                }
              }}
              role="checkbox"
              tabindex="0"
              aria-checked={$cantons.includes(code)}
            >
              <!-- Custom checkbox -->
              <div
                class="relative mr-2 flex h-3.5 w-3.5 items-center justify-center rounded border-2 transition-colors duration-200 {$cantons.includes(
                  code
                )
                  ? 'border-accent-500 bg-accent-500'
                  : 'border-gray-300 bg-white group-hover:border-accent-400'}"
              >
                {#if $cantons.includes(code)}
                  <svg
                    class="h-2.5 w-2.5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="3"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                {/if}
              </div>

              <!-- Canton code and name stacked -->
              <div class="min-w-0 flex-1 text-center">
                <div
                  class="text-sm font-semibold {$cantons.includes(code)
                    ? 'text-accent-900'
                    : 'text-gray-700 group-hover:text-gray-900'}"
                >
                  {code}
                </div>
                <div class="text-2xs text-gray-500">{$_(`cantons.${code}`)}</div>
              </div>
            </div>
          {/each}
        </div>
      </div>
    {/if}

    <!-- Region Presets -->
    {#if selectedType === 'regionPreset'}
      <div class="space-y-2">
        <label class="mb-2 block text-xs font-medium text-gray-700"
          >{$_('filters.canton.predefinedRegions')}</label
        >
        <div class={embedded ? 'grid gap-1.5 sm:grid-cols-2' : 'space-y-1'}>
          {#each regionPresets as preset (preset.id)}
            {@const isSelected = preset.cantons.every((c) => $cantons.includes(c))}
            <div
              class="group relative flex cursor-pointer items-center rounded-control border border-gray-200 bg-white px-3 py-2 transition-colors duration-200 hover:border-accent-300 hover:bg-accent-50 {isSelected
                ? 'border-accent-500 bg-accent-50'
                : ''}"
              on:click={() => {
                if (isSelected) {
                  // Remove all cantons from this region
                  cantons.set($cantons.filter((c) => !preset.cantons.includes(c)));
                } else {
                  // Add all cantons from this region
                  const newCantons = [...$cantons];
                  preset.cantons.forEach((c) => {
                    if (!newCantons.includes(c)) {
                      newCantons.push(c);
                    }
                  });
                  cantons.set(newCantons);
                }
              }}
              on:keydown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (isSelected) {
                    cantons.set($cantons.filter((c) => !preset.cantons.includes(c)));
                  } else {
                    const newCantons = [...$cantons];
                    preset.cantons.forEach((c) => {
                      if (!newCantons.includes(c)) {
                        newCantons.push(c);
                      }
                    });
                    cantons.set(newCantons);
                  }
                }
              }}
              role="checkbox"
              tabindex="0"
              aria-checked={isSelected}
            >
              <!-- Custom checkbox -->
              <div
                class="relative mr-2 flex h-3.5 w-3.5 items-center justify-center rounded border-2 transition-colors duration-200 {isSelected
                  ? 'border-accent-500 bg-accent-500'
                  : 'border-gray-300 bg-white group-hover:border-accent-400'}"
              >
                {#if isSelected}
                  <svg
                    class="h-2.5 w-2.5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="3"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                {/if}
              </div>

              <div class="flex-1">
                <span
                  class="text-xs font-medium {isSelected
                    ? 'text-accent-900'
                    : 'text-gray-700 group-hover:text-gray-900'}">{$_(`regions.${preset.id}`)}</span
                >
                <span class="ml-2 text-2xs text-gray-500">({preset.cantons.join(', ')})</span>
              </div>
            </div>
          {/each}
        </div>
      </div>
    {/if}

    <!-- Languages Filter -->
    {#if selectedType === 'languages'}
      <div class="space-y-2">
        <div class="mb-2 flex items-center justify-between">
          <label class="block text-xs font-medium text-gray-700"
            >{$_('filters.languages.title')}</label
          >
          <span class="text-2xs text-gray-500"
            >{$languages.length} {$_('buttons.select').toLowerCase()}</span
          >
        </div>

        <!-- Language checkboxes -->
        <div class={embedded ? 'grid gap-1.5 grid-cols-2 sm:grid-cols-4' : 'space-y-1'}>
          {#each ['de', 'fr', 'it', 'rm'] as lang}
            <div
              class="group relative flex cursor-pointer items-center rounded-control border border-gray-200 bg-white px-2 py-1.5 transition-colors duration-200 hover:border-accent-300 hover:bg-accent-50 {$languages.includes(
                lang
              )
                ? 'border-accent-500 bg-accent-50'
                : ''}"
              on:click={() => {
                const isSelected = $languages.includes(lang);
                if (isSelected) {
                  languages.set($languages.filter((l) => l !== lang));
                } else {
                  languages.set([...$languages, lang]);
                }
              }}
              on:keydown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  const isSelected = $languages.includes(lang);
                  if (isSelected) {
                    languages.set($languages.filter((l) => l !== lang));
                  } else {
                    languages.set([...$languages, lang]);
                  }
                }
              }}
              role="checkbox"
              tabindex="0"
              aria-checked={$languages.includes(lang)}
            >
              <!-- Custom checkbox -->
              <div
                class="relative mr-2 flex h-3.5 w-3.5 items-center justify-center rounded border-2 transition-colors duration-200 {$languages.includes(
                  lang
                )
                  ? 'border-accent-500 bg-accent-500'
                  : 'border-gray-300 bg-white group-hover:border-accent-400'}"
              >
                {#if $languages.includes(lang)}
                  <svg
                    class="h-2.5 w-2.5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="3"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                {/if}
              </div>

              <!-- Label -->
              <span
                class="flex-1 text-xs {$languages.includes(lang)
                  ? 'font-medium text-accent-900'
                  : 'text-gray-700 group-hover:text-gray-900'}"
                >{$_(`filters.languages.${lang}`)}</span
              >
            </div>
          {/each}
        </div>
      </div>
    {/if}
  {/if}
</div>
